/**
 * Favorites Service
 * Handles user favorites API calls
 */

import api from './api';
import { getAxiosErrorMessage } from '../utils/error';

// ============================================
// Type Definitions
// ============================================

export interface FavoriteUser {
  id: number;
  username: string;
  displayName: string;
  avatarUrl?: string;
  isLive?: boolean;
  favoritedAt: string;
}

export interface FavoritedByUser extends FavoriteUser {
  isFavoritedBack: boolean;
}

interface GetFavoritesSuccessResponse {
  success: true;
  favorites: FavoriteUser[];
}

interface GetFavoritesErrorResponse {
  success: false;
  error: string;
}

type GetFavoritesResponse = GetFavoritesSuccessResponse | GetFavoritesErrorResponse;

interface GetFavoritedBySuccessResponse {
  success: true;
  favorites: FavoritedByUser[];
}

type GetFavoritedByResponse = GetFavoritedBySuccessResponse | GetFavoritesErrorResponse;

interface AddFavoriteSuccessResponse {
  success: true;
  favorite: { id: number; favoritedAt: string };
  message: string;
}

interface AddFavoriteErrorResponse {
  success: false;
  error: string;
}

type AddFavoriteResponse = AddFavoriteSuccessResponse | AddFavoriteErrorResponse;

interface RemoveFavoriteSuccessResponse {
  success: true;
  message: string;
}

interface RemoveFavoriteErrorResponse {
  success: false;
  error: string;
}

type RemoveFavoriteResponse = RemoveFavoriteSuccessResponse | RemoveFavoriteErrorResponse;

// ============================================
// Service Functions
// ============================================

/**
 * Get the current user's favorites list
 */
export const getMyFavorites = async (): Promise<GetFavoritesResponse> => {
  try {
    const response = await api.get('/me/favorites');
    return { success: true, favorites: response.data.favorites || [] };
  } catch (error) {
    return {
      success: false,
      error: getAxiosErrorMessage(error, 'Failed to fetch favorites', {
        log: 'FavoritesService.getMyFavorites',
      }),
    };
  }
};

/**
 * Add a user to favorites
 */
export const addFavorite = async (userId: number): Promise<AddFavoriteResponse> => {
  try {
    const response = await api.post(`/me/favorites/${userId}`);
    return {
      success: true,
      favorite: response.data.favorite,
      message: response.data.message || 'Favorite added successfully',
    };
  } catch (error) {
    return {
      success: false,
      error: getAxiosErrorMessage(error, 'Failed to add favorite', {
        log: 'FavoritesService.addFavorite',
      }),
    };
  }
};

/**
 * Remove a user from favorites
 */
export const removeFavorite = async (userId: number): Promise<RemoveFavoriteResponse> => {
  try {
    const response = await api.delete(`/me/favorites/${userId}`);
    return { success: true, message: response.data.message || 'Favorite removed successfully' };
  } catch (error) {
    return {
      success: false,
      error: getAxiosErrorMessage(error, 'Failed to remove favorite', {
        log: 'FavoritesService.removeFavorite',
      }),
    };
  }
};

/**
 * Get users who have favorited the current user
 */
export const getMyFavoritedBy = async (): Promise<GetFavoritedByResponse> => {
  try {
    const response = await api.get('/me/favorited-by');
    return { success: true, favorites: response.data.favoritedBy || [] };
  } catch (error) {
    return {
      success: false,
      error: getAxiosErrorMessage(error, 'Failed to fetch favorited-by list', {
        log: 'FavoritesService.getMyFavoritedBy',
      }),
    };
  }
};

/**
 * Toggle favorite status for a user
 */
export const toggleFavorite = async (
  userId: number,
  isFavorited: boolean,
): Promise<AddFavoriteResponse | RemoveFavoriteResponse> => {
  if (isFavorited) {
    return removeFavorite(userId);
  } else {
    return addFavorite(userId);
  }
};
